/**
 * Aperçu visuel d'un artifact (§3.5).
 *
 * Le comparatif ligne à ligne annonce « 2 lignes ajoutées » ; il ne dit pas si
 * l'application a changé d'allure. Ce composant l'affiche réellement, pour que
 * son auteur — qui le plus souvent ne lit pas le code — compare ce qu'il voit.
 *
 * ## Isolement
 *
 * Le code affiché vient d'un utilisateur : il ne doit atteindre ni la session, ni
 * les données du portail, ni les autres onglets. Trois barrières s'y emploient :
 *
 * - `sandbox="allow-scripts"` **sans** `allow-same-origin` : le document reçoit
 *   une origine opaque, sans cookie, sans accès au parent, sans stockage. Les
 *   deux valeurs sont indissociables — les réunir rendrait le bac à sable
 *   inopérant, et le navigateur le signale lui-même ;
 * - le contenu n'est pas injecté par `srcdoc` mais transmis par `postMessage` à
 *   une page servie par l'API. Un document `srcdoc` héritant de la politique de
 *   sécurité du portail, les scripts en ligne de l'artifact y seraient bloqués et
 *   l'aperçu d'un jeu montrerait une zone vide. La page hôte, elle, reçoit la
 *   politique des applications déployées : l'aperçu montre donc exactement ce que
 *   verra le visiteur ;
 * - le contenu ne transite jamais par le serveur, qui n'a rien à en stocker.
 *
 * ## Échelle
 *
 * L'artifact est rendu dans une fenêtre de largeur `VIEWPORT_WIDTH` puis réduit
 * géométriquement. Sans cela, une page conçue pour un écran large déclencherait
 * ses règles `@media` mobiles dans un cadre de 400 pixels, et deux aperçus côte à
 * côte montreraient une mise en page que personne ne verra jamais.
 */

import { AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { previewSource } from '../lib/preview';

/** Chemin de la page hôte servie par l'API, avec sa propre politique de sécurité. */
const FRAME_URL = '/api/preview-frame';

/** Jeton de reconnaissance des messages, identique à celui de la page hôte. */
const MESSAGE_TOKEN = 'portail-apercu';

/**
 * Largeur de rendu simulée, en pixels CSS.
 *
 * Un poste de travail ordinaire : c'est la mise en page que verra la majorité des
 * visiteurs, et donc celle qu'il faut comparer.
 */
const VIEWPORT_WIDTH = 1280;

/** Hauteur de rendu simulée, dans la proportion d'un écran courant. */
const VIEWPORT_HEIGHT = 800;

export interface ArtifactPreviewProps {
  /** Code à afficher : contenu transformé déplié, ou code brut collé. */
  code: string | null;
  /** Titre du volet, généralement le numéro de version. */
  label: string;
  /**
   * Hauteur du cadre visible, en pixels. L'échelle s'en déduit : le contenu est
   * toujours rendu en `VIEWPORT_WIDTH × VIEWPORT_HEIGHT` puis réduit.
   */
  height?: number;
}

/** Rendu isolé d'un artifact, à l'échelle. */
export function ArtifactPreview({ code, label, height = 360 }: ArtifactPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(false);
  /** Incrémenté pour recharger le cadre : un aperçu ne s'écrit qu'une fois. */
  const [generation, setGeneration] = useState(0);

  const source = previewSource(code);
  const html = source.previewable ? source.html : null;

  // La largeur disponible commande l'échelle. Elle est mesurée plutôt que
  // supposée : le composant sert aussi bien en pleine largeur qu'en demi.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(measured);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /**
   * Transmet le contenu dès que la page hôte s'annonce prête.
   *
   * L'échange est un aller-retour et non un envoi à l'aveugle : poster avant que
   * le cadre n'ait installé son écouteur perdrait le message sans trace.
   */
  useEffect(() => {
    if (html === null) return undefined;
    setReady(false);

    const onMessage = (event: MessageEvent): void => {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const payload = event.data as { jeton?: string; pret?: boolean } | null;
      if (!payload || payload.jeton !== MESSAGE_TOKEN || payload.pret !== true) return;
      // Origine opaque du bac à sable : `'*'` est la seule cible acceptable, et
      // elle est sans risque — le contenu envoyé est celui que le cadre affiche.
      frame.contentWindow?.postMessage({ jeton: MESSAGE_TOKEN, html }, '*');
      setReady(true);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [html, generation]);

  const reload = useCallback(() => setGeneration((value) => value + 1), []);

  if (!source.previewable) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <p className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {source.reason}
        </p>
      </div>
    );
  }

  const scale = width > 0 ? width / VIEWPORT_WIDTH : 0;
  // La hauteur du cadre suit l'échelle : le contenu n'est jamais tronqué
  // arbitrairement, il est réduit dans la même proportion que la largeur.
  const scaledHeight = Math.min(height, VIEWPORT_HEIGHT * scale || height);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1 rounded text-xs text-slate-500 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-slate-400 dark:hover:text-slate-100"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          Rejouer
        </button>
      </div>

      <div
        ref={containerRef}
        style={{ height: scaledHeight }}
        className="relative overflow-hidden rounded-md border border-slate-300 bg-white dark:border-slate-700"
      >
        {scale > 0 ? (
          <iframe
            key={generation}
            ref={frameRef}
            src={FRAME_URL}
            title={`Aperçu de ${label}`}
            // `allow-scripts` sans `allow-same-origin` : le contenu s'exécute,
            // mais dans une origine opaque, hors d'atteinte du portail.
            sandbox="allow-scripts"
            // La page ne doit pas pouvoir prendre le focus ni faire défiler la
            // page qui l'entoure. Le défilement interne, lui, reste utile.
            scrolling="no"
            style={{
              width: VIEWPORT_WIDTH,
              height: VIEWPORT_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
            className="pointer-events-none absolute left-0 top-0 border-0"
          />
        ) : null}

        {!ready ? (
          <p className="absolute inset-0 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
            Préparation de l'aperçu…
          </p>
        ) : null}
      </div>

      <p className="mt-1 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
        Rendu à l'échelle d'un écran de {VIEWPORT_WIDTH} px, sans interaction.
      </p>
    </div>
  );
}
